import type { SourceDialogMode } from '../SourceCreate.types';

/**
 * Acronis-specific type definitions.
 *
 * Separates Acronis-specific domain models from the general SourceCreate types,
 * improving maintainability and reducing complexity in shared types.
 */

/**
 * Available Acronis category (log type) options.
 * These are the ingestion categories supported by Acronis.
 */
export enum AcronisCategoryOption {
  AlertManager = 'AlertManager',
  EventManager = 'EventManager',
  Topics = 'Topics'
}

/**
 * Acronis topic object returned from the API.
 * Represents an available topic for ingestion.
 */
export type AcronisTopic = {
  id: string;
  name?: string;
  description?: string;
};

/**
 * Acronis form values extracted from React Hook Form.
 * Used for validation and credential-based API calls.
 */
export type AcronisFormValues = {
  base_url?: string;
  client_id?: string;
  client_secret?: string;
  category?: string[];
  topics?: string[]; // Selected topic IDs
};

/**
 * Credentials state for tracking which credentials have been loaded.
 * Used to prevent duplicate API calls when credentials haven't changed.
 */
export type AcronisCredentialsState = {
  base_url: string;
  client_id: string;
  client_secret: string;
};

export type AcronisTopicsProps = {
  /** Dialog mode: New (create) or Edit (modify existing) */
  mode?: SourceDialogMode;

  /** Whether source type is currently being reset (should hide form) */
  isResettingSourceType?: boolean;
};

export type AcronisTopicOption = { id: string; name: string };

export type InFlightTopicsRequest = {
  requestId: number;
  promise: Promise<AcronisTopicOption[]>;
};

export type UseAcronisTopicsLoaderParams = {
  /** Source type ID used for conditional logic (e.g., to check if this is ACRONIS source) */
  sourceTypeId?: number;
  /** Dialog mode used to bootstrap topic loading for edit flows with prefilled credentials */
  mode?: SourceDialogMode;
};

export type UseAcronisTopicsLoaderReturn = {
  /** Available topics returned from API, ready to populate dropdown options */
  topics: AcronisTopicOption[];
  /** True while Topics API call is in progress */
  isLoading: boolean;
  /** Error message if Topics API call failed */
  error: string | null;
  /** True after at least one successful topics API response */
  hasSuccessfulTopicsResponse: boolean;
  /** Call this from credential field onBlur handlers to trigger Topics API */
  handleCredentialBlur: () => Promise<boolean>;
  /** Call this to clear topics (e.g., when Event Manager is deselected) */
  clearTopics: () => void;
};

export type UseAcronisCredentialBlurHandlerParams = {
  onCredentialBlur: () => Promise<boolean>;
};
